// @ts-nocheck
if (typeof ShippingEstimator === 'undefined') {
  class ShippingEstimator extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.currentPostcode = '';
      this.availableDates = [];
      this.isLoading = false;
      this.postcodeConfirmed = false;
      this.enableOutOfStock = true;
      this.deliveryDateRequestTime = null;
      this.hasCheckedExpiredDate = false;
      this.minimumOrderAmount = 0;
      this.cartTotal = 0;
      this.cartUpdateUnsubscriber = null;
      this.validationResponseData = null;
      this.productTotalsValid = true;
      this.shopUrl = Shopify.shop;
      this.currencySymbol = '$';
      this.hasCriticalError = false;
      this._requestQueue = Promise.resolve();
      this.cachedElements = {};
    }

    async connectedCallback() {
      this.enableOutOfStock = this.getAttribute('data-enable-out-of-stock') === 'true';
      this.deliveryDateRequestTime = this.getAttribute('data-delivery-date-request-time');

      const currencyAttr = this.getAttribute('data-currency-symbol');
      if (currencyAttr) {
        this.currencySymbol = currencyAttr;
      }

      this.loadColorSettings();
      this.loadMessageSettings();
      this.render();
      this.setupEventListeners();
      await this.loadInitialData();
      this.setupCartUpdateListener();
      this.updateCheckoutButtonState();
      this.checkCartDrawerState();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }

      if (this.cartObserver) {
        this.cartObserver.disconnect();
      }
      if (this.drawerObserver) {
        this.drawerObserver.disconnect();
      }

      this.cachedElements = {};
    }

    getElement(selector) {
      if (!this.cachedElements[selector]) {
        this.cachedElements[selector] = this.shadowRoot.querySelector(selector);
      }
      return this.cachedElements[selector];
    }

    loadColorSettings() {
      this.colorSettings = {
        textColor: this.getAttribute('data-text-color') || '#000000',
        buttonBackground: this.getAttribute('data-button-background') || '#04529E',
        buttonForeground: this.getAttribute('data-button-foreground') || '#FFFFFF',
        pillBackground: this.getAttribute('data-pill-background') || '#EB0000',
        pillText: this.getAttribute('data-pill-text') || '#FFFFFF',
        successBackground: this.getAttribute('data-success-background') || '#d4edda',
        successText: this.getAttribute('data-success-text') || '#155724',
        errorBackground: this.getAttribute('data-error-background') || '#f8d7da',
        errorText: this.getAttribute('data-error-text') || '#721c24',
        infoBackground: this.getAttribute('data-info-background') || '#d1ecf1',
        infoText: this.getAttribute('data-info-text') || '#0c5460'
      };
    }

    loadMessageSettings() {
      const getMessageSetting = (attrName, settingKey, defaultValue) => {
        const attrValue = this.getAttribute(attrName);
        if (attrValue) return attrValue;
        if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.settings) {
          return Shopify.theme.settings[settingKey] || defaultValue;
        }
        return defaultValue;
      };

      this.messageSettings = {
        minimumOrderAmount: getMessageSetting(
          'data-minimum-order-amount-message',
          'minimum_order_amount_message',
          'Minimum order amount is [minimum amount].\nPlease add [current amount] more to your cart to proceed.'
        ),
        minimumSubscriptionAmount: getMessageSetting(
          'data-minimum-subscription-amount-message',
          'minimum_subscription_amount_message',
          'Subscription products total must be greater than [minimum subscription amount].\nCurrent: [current subscription amount]. Please add [remaining subscription amount] more in subscription products.'
        ),
        minimumOneTimeAmount: getMessageSetting(
          'data-minimum-one-time-amount-message',
          'minimum_one_time_amount_message',
          'Regular products total must be greater than [minimum one-time amount].\nCurrent: [current one-time amount]. Please add [remaining one-time amount] more in regular products.'
        )
      };
    }

    replaceMessagePlaceholders(template, replacements) {
      let message = template;
      Object.keys(replacements).forEach(key => {
        const placeholder = `[${key}]`;
        const value = replacements[key];
        const formattedValue = `${this.currencySymbol}${value}`;
        const boldValue = `<strong>${formattedValue}</strong>`;
        message = message.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), boldValue);
      });

      return message;
    }

    async loadInitialData() {
      this.currentPostcode = '';
      this.postcodeConfirmed = false;
      this.getElement('#postcode-display').value = '';
      this.getElement('#delivery-date-display').textContent = 'Select Delivery Date';

      if (this.enableOutOfStock) {
        this.getElement('#out-of-stock-display').textContent = 'Please select option';
      }

      await this.getCartData();
      this.updateDeliveryDateState();
    }

    async loadAvailableDates() {
      if (!this.currentPostcode) return;

      try {
        const result = await this.validatePostcodeAPI(this.currentPostcode);
        this.processValidationResult(result);
      } catch (error) {
        console.error('Failed to load available dates:', error);
        this.availableDates = [];
        this.setCalendarToEarliestAvailableDate();
        this.renderCalendar();
      }
    }

    async validatePostcodeAPI(postcode) {
      let subscriptionTotal = 0;
      let regularTotal = 0;
      let cartTotal = 0;

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      for (const item of cart.items) {
        const lineTotal = item.final_line_price;
        const hasSellingPlan = !!item.selling_plan_allocation;

        if (hasSellingPlan) {
          subscriptionTotal += lineTotal;
        } else {
          regularTotal += lineTotal;
        }
        cartTotal += lineTotal;
      }

      const cartItems = await this.getCartItems();
      const requestBody = {
        "product_id": this.formatCartItemsForAPI(cartItems)
      };

      const requestOptions = {
        method: "POST",
        body: JSON.stringify(requestBody),
        redirect: "follow"
      };

      const response = await fetch(`https://pcv-brownes.cultivated.agency/postcode_validator/cart_response.php?shop=${this.shopUrl}&postcode=${postcode}&cart_total=${cartTotal}&subscription_total=${subscriptionTotal}&regular_total=${regularTotal}`, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Validation API failed with status: ${response.status}`);
      }
      
      return await response.text();
    }

    enqueueRequest(task) {
      this._requestQueue = this._requestQueue.then(() => task()).catch(err => {
        console.warn("Queue note :", err);
      });
      return this._requestQueue;
    }

    processValidationResult(result, showMessage = true) {
      if (result.startsWith('Valid||||||')) {
        const parts = result.split('|||');
        const datesSection = result.split('Valid||||||')[1]?.split('|||')[0];
        const minimumOrderAmount = parseFloat(parts[3] || '0');
        const deliveryMessage = parts[4] || '';
        const deliveryGroup = parts[5] || '';
        const subscriptionProductTotal = parseFloat(parts[6] || '');
        const regularProductTotal = parseFloat(parts[7] || '');

        this.minimumOrderAmount = minimumOrderAmount;
        this.validationResponseData = {
          isValid: true,
          minimumOrderAmount: minimumOrderAmount,
          deliveryMessage: deliveryMessage,
          deliveryGroup: deliveryGroup,
          subscriptionProductTotal: subscriptionProductTotal,
          regularProductTotal: regularProductTotal
        };

        if (datesSection) {
          this.availableDates = datesSection.split(',').map(date => date.trim());
          this.setCalendarToEarliestAvailableDate();
          this.renderCalendar();
        }

        if (showMessage) {
          this.validateCartTotal()
            .then(() => {
              this.updateCheckoutButtonState();
            })
            .catch(error => {
              console.error('Error validating cart total:', error);
            });
        }

        return true;
      }
      return false;
    }

    render() {
      const deliveryPostcodeText = this.getAttribute('data-postcode-text') || 'Delivery Postcode';
      const deliveryDateText = this.getAttribute('data-date-text') || 'Delivery Date';
      const outOfStockText = this.getAttribute('data-out-of-stock-text') || 'Out of Stock Preference';
      const buttonText = this.getAttribute('data-button-text') || 'Confirm';

      const outOfStockSection = this.enableOutOfStock ? `
    <div class="estimator-item" id="out-of-stock-item">
      <span class="item-number">3</span>
      <div class="item-content">
        <span class="item-label">${outOfStockText}</span>
        <div class="dropdown-container">
          <button class="dropdown-trigger" id="stock-dropdown-trigger">
            <span id="out-of-stock-display">Please select option</span>
            <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 1.5L5 5.5L1 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="dropdown-menu" id="stock-dropdown-menu">
            <ul>
              <li data-value="">Please select option</li>
              <li data-value="Refund the amount of out-of stock items">Refund the amount of out-of stock items</li>
              <li data-value="Store credit for same value for the out-stock item/s">Store credit for same value for the out-stock item/s</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  ` : '';

      this.shadowRoot.innerHTML = `
      <style>
        :host {
          --primary-color: ${this.colorSettings.buttonBackground};
          --secondary-color: ${this.colorSettings.pillBackground};
          --primary-color-hover: ${this.colorSettings.buttonBackground}CC;
          --font-color: ${this.colorSettings.buttonForeground};
          --text-color: ${this.colorSettings.textColor};
          --pill-background: ${this.colorSettings.pillBackground};
          --pill-text: ${this.colorSettings.pillText};
          --success-background: ${this.colorSettings.successBackground};
          --success-text: ${this.colorSettings.successText};
          --error-background: ${this.colorSettings.errorBackground};
          --error-text: ${this.colorSettings.errorText};
          --info-background: ${this.colorSettings.infoBackground};
          --info-text: ${this.colorSettings.infoText};
          --disabled-color: #ccc;
          --border-color: #D0D0D0;
          --border-color-light: #e8e8e8;
          --font-weight-bold: 700;
          --font-weight-medium: 500;
          --font-size-small: calc(var(--font-body-scale) * 1.4rem);
          --font-size-normal: calc(var(--font-body-scale) * 1.6rem);
          --font-size-large: calc(var(--font-body-scale) * 1.8rem);
          --line-height-normal: 1.2;
          --padding-small: 6px;
          --padding-medium: 10px 12px;
          --radius-circle: 50%;
          --radius-soft: 6px;
          --radius-pill: 25px;
          --shadow-elevated: 0 4px 12px rgba(0, 0, 0, 0.15);
          --transition-default: background-color 0.3s ease;
          display: block;
          font-family: inherit;
          margin-bottom: 1rem;
          position: relative;
          z-index: 999;
        }

        .shipping-estimator * {
          letter-spacing: 0;
        }

        .estimator-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0;
        }

        .estimator-item:nth-of-type(2) {
          padding-top: 0;
        }

        .estimator-item:last-of-type {
          padding-bottom: 0;
          border-bottom: none;
        }

        .estimator-item.message-container-wrapper {
          display: none;
        }

        .estimator-item.message-container-wrapper.active {
          display: block;
          padding: 0 0 16px 0;
          border-bottom: none;
        }

        .estimator-item.disabled {
          filter: grayscale(1);
          opacity: 0.5;
          pointer-events: none;
        }

        .item-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background-color: var(--pill-background);
          color: #fff;
          border-radius: var(--radius-circle);
          font-weight: var(--font-weight-bold);
          margin-right: 8px;
          flex-shrink: 0;
          font-size: var(--font-size-small);
        }

        .item-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          text-align: left;
        }

        .item-label {
          font-size: var(--font-size-small);
          line-height: var(--line-height-normal);
          color: var(--text-color);
        }

        .item-value {
          margin-right: 8px;
        }

        .postcode-input-wrapper {
          display: flex;
          align-items: center;
        }

        .postcode-input {
          border-radius: var(--radius-pill) 0 0 var(--radius-pill);
          width: 46px;
          text-align: center;
          border: 1px solid var(--border-color);
          border-right: none;
          padding: 9px 4px 9px 9px;
          outline: none;
          font-size: var(--font-size-normal);
          flex: 1;
        }

        .postcode-input:focus {
          border-color: var(--primary-color);
        }

        .change-button {
          background-color: var(--primary-color);
          color: white;
          border: none;
          font-weight: var(--font-weight-medium);
          cursor: pointer;
          padding: var(--padding-medium);
          border-radius: 0 var(--radius-pill) var(--radius-pill) 0;
          font-size: var(--font-size-normal);
          transition: var(--transition-default);
        }

        .change-button:hover {
          background: var(--primary-color-hover);
        }

        .change-button:disabled {
          background: var(--disabled-color);
          cursor: not-allowed;
        }

        .dropdown-container {
          position: relative;
          display: inline-block;
        }

        .dropdown-trigger {
          font-size: var(--font-size-small);
          line-height: var(--line-height-normal);
          background: none;
          text-align: right;
          border: none;
          color: var(--text-color);
          cursor: pointer;
          display: flex;
          justify-content: flex-end;
        align-items: center;
          gap: 4px;
          min-width: 155px;
        }

        .dropdown-trigger svg {
          color: var(--primary-color);
          width: 10px;
          height: 10px;
          flex-shrink: 0;
          transition: transform 0.2s;
        }

        .dropdown-trigger.open svg {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          bottom: 100%;
          top: auto;
          right: 0;
          background: white;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-soft);
          box-shadow: var(--shadow-elevated);
          z-index: 10000;
          min-width: 250px;
          max-height: 300px;
          overflow-y: auto;
          display: none;
          left: auto;
          z-index: 999999;
        }

        .dropdown-menu.open {
          display: block;
        }

        .dropdown-menu ul {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .dropdown-menu li {
          padding: var(--padding-medium);
          cursor: pointer;
          border-bottom: 1px solid var(--border-color);
          font-size: var(--font-size-normal);
          line-height: var(--line-height-normal);
        }

        .dropdown-menu li:last-child {
          border-bottom: none;
        }

        .dropdown-menu li:hover {
          background-color: var(--border-color-light);
        }

        .loading {
          opacity: 0.6;
          pointer-events: none;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
          padding: 6px;
        }

        .calendar-header {
          font-size: var(--font-size-normal);
          line-height: var(--line-height-normal);
          text-align: center;
          font-weight: var(--font-weight-bold);
          border-radius: var(--radius-soft);
          padding: var(--padding-small);
          background-color: var(--border-color-light);
          color: var(--text-color);
        }

        .calendar-day {
          font-size: var(--font-size-small);
          line-height: var(--line-height-normal);
          text-align: center;
          padding: var(--padding-small);
          cursor: pointer;
          border-radius: var(--radius-soft);
          transition: background-color 0.2s;
          color: var(--text-color);
        }

        .calendar-day:hover {
          background-color: var(--border-color-light);
        }

        .calendar-day.available {
          background-color: var(--success-background);
          color: var(--success-text);
        }
        
        .calendar-day.available:hover {
          background-color: var(--success-text);
          color: #fff;
        }

        .calendar-day.selected {
          background-color: var(--success-text);
          color: #fff;
          font-weight: var(--font-weight-bold);
        }

        .calendar-day.disabled {
          color: var(--disabled-color);
          cursor: not-allowed;
        }

        .calendar-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 6px;
        }

        .nav-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: var(--radius-soft);
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
        }

        .nav-button:hover {
          background-color: var(--border-color-light);
        }

        .nav-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .nav-button svg {
          width: 16px;
          height: 16px;
        }

        .month-year {
          font-size: var(--font-size-normal);
          line-height: var(--line-height-normal);
          font-weight: var(--font-weight-bold);
          flex: 1;
          text-align: center;
          color: var(--text-color);
        }

        .message-container {
          width: 100%;
        }

        .message {
          padding: 8px 12px;
          border-radius: var(--radius-soft);
          font-size: var(--font-size-normal);
          line-height: var(--line-height-normal);
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
          white-space: normal;
          box-sizing: border-box
        }

        .message a {
          word-break: break-all;
          color: var(--primary-color);
          font-weight: var(--font-weight-bold);
        }

        .delivery-group {
          font-size: var(--font-size-normal);
          font-weight: var(--font-weight-bold);
          margin-bottom: 4px;
          color: inherit;
        }

        #delivery-date-display {
          width: max-content;
          color: var(--primary-color);
        }

        .delivery-message {
          font-size: var(--font-size-small);
          color: inherit;
        }

        .message.success {
          background-color: var(--success-background);
          color: var(--success-text);
        }

        .message.error {
          background-color: var(--error-background);
          color: var(--error-text);
        }

        .message.info {
          background-color: var(--info-background);
          color: var(--info-text);
        } 
      </style>

      <div class="shipping-estimator">

      <div class="estimator-item message-container-wrapper">
        <div class="message-container" id="message-container"></div>
      </div>

        <div class="estimator-item">
          <span class="item-number">1</span>
          <div class="item-content">
            <span class="item-label">${deliveryPostcodeText}</span>
            <div class="postcode-input-wrapper">
              <input type="text" class="postcode-input" id="postcode-display" pattern="[0-9]*" inputmode="numeric" maxlength="12">
              <button class="change-button" id="change-postcode">${buttonText}</button>
            </div>
          </div>
        </div>

        <div class="estimator-item" id="delivery-date-item">
          <span class="item-number">2</span>
          <div class="item-content">
            <span class="item-label">${deliveryDateText}</span>
            <div class="dropdown-container">
              <button class="dropdown-trigger" id="date-dropdown-trigger">
                <span id="delivery-date-display"></span>
                <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 1.5L5 5.5L1 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <div class="dropdown-menu" id="date-dropdown-menu">
                <div class="calendar-navigation">
                  <button class="nav-button" id="prev-month" aria-label="Previous month">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <span class="month-year" id="month-year">July 2025</span>
                  <button class="nav-button" id="next-month" aria-label="Next month">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div class="calendar-grid" id="calendar-grid"></div>
              </div>
            </div>
          </div>
        </div>

        ${outOfStockSection}
      </div>
    `;
    }

    setupEventListeners() {
      const postcodeInput = this.getElement('#postcode-display');
      const changePostcodeBtn = this.getElement('#change-postcode');
      const dateDropdownTrigger = this.getElement('#date-dropdown-trigger');
      const dateDropdownMenu = this.getElement('#date-dropdown-menu');
      const prevMonthBtn = this.getElement('#prev-month');
      const nextMonthBtn = this.getElement('#next-month');
      const stockDropdownTrigger = this.getElement('#stock-dropdown-trigger');
      const stockDropdownMenu = this.getElement('#stock-dropdown-menu');

      postcodeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '');
        this.updateDeliveryDateState();
      });

      postcodeInput.addEventListener('keyup', (e) => {
        if (e.key === 'Enter') this.handlePostcodeChange();
      });

      changePostcodeBtn.addEventListener('click', () => this.handlePostcodeChange());

      dateDropdownTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const deliveryDateItem = this.getElement('#delivery-date-item');
        if (deliveryDateItem.classList.contains('disabled')) {
          return;
        }
        this.toggleDropdown(dateDropdownTrigger, dateDropdownMenu);
      });

      prevMonthBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.navigateMonth(-1);
      });

      nextMonthBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.navigateMonth(1);
      });

      if (this.enableOutOfStock) {
        stockDropdownTrigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleDropdown(stockDropdownTrigger, stockDropdownMenu);
        });

        stockDropdownMenu.addEventListener('click', (e) => {
          if (e.target.tagName === 'LI') {
            const value = e.target.dataset.value;
            const text = e.target.textContent;
            this.updateOutOfStockPreference(value, text);
            this.closeDropdown(stockDropdownTrigger, stockDropdownMenu);
          }
        });
      }

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeAllDropdowns();
        }
      });

      const today = new Date();
      this.currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      this.renderCalendar();
    }

    toggleDropdown(trigger, menu) {
      const isOpen = menu.classList.contains('open');
      this.closeAllDropdowns();

      if (!isOpen) {
        trigger.classList.add('open');
        menu.classList.add('open');

        this.adjustDropdownPosition(trigger, menu);

        setTimeout(() => {
          menu.addEventListener('click', (e) => {
            e.stopPropagation();
          }, { once: false });
        }, 10);
      }
    }

    adjustDropdownPosition(trigger, menu) {
      const isInCartDrawer = this.closest('cart-drawer') !== null;

      menu.style.zIndex = isInCartDrawer ? '10001' : '10000';
      menu.style.position = 'absolute';
      menu.style.top = 'auto';
      menu.style.bottom = '100%';
      menu.style.right = '0';
      menu.style.left = 'auto';

      setTimeout(() => {
        const rect = menu.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        if (rect.right > viewportWidth) {
          menu.style.right = '0';
          menu.style.left = 'auto';
        }
      }, 10);
    }

    closeDropdown(trigger, menu) {
      trigger.classList.remove('open');
      menu.classList.remove('open');
    }

    closeAllDropdowns() {
      const triggers = this.shadowRoot.querySelectorAll('.dropdown-trigger');
      const menus = this.shadowRoot.querySelectorAll('.dropdown-menu');

      triggers.forEach(trigger => trigger.classList.remove('open'));
      menus.forEach(menu => menu.classList.remove('open'));
    }

    async handlePostcodeChange() {
      const postcodeInput = this.getElement('#postcode-display');
      const newPostcode = postcodeInput.value.trim();

      if (!newPostcode) {
        this.showMessage('Please enter a postcode', 'error');
        return;
      }

      await this.clearDeliveryDate();

      this.setLoading(true);
      this.showMessage('Checking postcode...', 'info');

      try {
        await this.validatePostcode(newPostcode);
        this.postcodeConfirmed = true;
        this.updateDeliveryDateState();
      } catch (error) {
        this.postcodeConfirmed = false;
        this.updateDeliveryDateState();
      } finally {
        this.setLoading(false);
      }
    }

    async validatePostcode(postcode) {
      const result = await this.validatePostcodeAPI(postcode);

      if (this.processValidationResult(result)) {
        this.currentPostcode = postcode;
        this.getElement('#postcode-display').value = postcode;

        await this.getCartData();
        await this.validateCartTotal();
        this.updateCheckoutButtonState();

        try {
          await this.updateCartAttributes({
            'Postcode': postcode,
            'Delivery-Date-Request-Time': new Date().toISOString()
          });
          this.hasCriticalError = false;
        } catch (error) {
          this.hasCriticalError = true;
          this.showMessage(
            `Unable to save postcode. Please refresh the page and try again, or contact support if the issue persists.`,
            'error'
          );
          this.updateCheckoutButtonState();
          throw error;
        }
      } else {
        this.showMessage(result, 'error');
        const deliveryDateDisplay = this.getElement('#delivery-date-display');
        deliveryDateDisplay.textContent = 'Select Delivery Date';
        throw new Error('Invalid postcode');
      }
    }

    async getCartItems() {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.items;
    }

    async getCartData() {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.cartTotal = cart.total_price / 100;
      return cart;
    }

    setupCartUpdateListener() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          await this.getCartData();
          await this.clearShippingData();
          await this.validateCartTotal();
          this.updateDeliveryDateState();
          this.updateCheckoutButtonState();
        });
      }

      this.setupCartChangeListeners();
    }

    setupCartChangeListeners() {
      const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      };

      const handleCartChange = debounce(async () => {
        await this.getCartData();
        
        if (this.currentPostcode && this.postcodeConfirmed) {
          if (this.cartTotal > 0) {
            await this.validateCartTotal();
          }
        } else {
          await this.clearShippingData();
        }

        this.updateDeliveryDateState();
        this.updateCheckoutButtonState();
      }, 300);

      document.addEventListener('cart:updated', handleCartChange);
      document.addEventListener('cart:refresh', handleCartChange);

      document.body.addEventListener('submit', (e) => {
        if (e.target.matches('#cart, #CartDrawer-Form, form[action="/cart/add"]')) {
          handleCartChange();
        }
      });

      document.body.addEventListener('change', (e) => {
        if (e.target.matches('.quantity__input, [data-cart-quantity], [name="updates[]"]')) {
          handleCartChange();
        }
      });

      document.body.addEventListener('click', (e) => {
        if (e.target.closest('.cart-remove-button, [data-variant-id], .cart__remove')) {
          handleCartChange();
        }
      });

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        if (this.drawerObserver) this.drawerObserver.disconnect();

        this.drawerObserver = new MutationObserver((mutations) => {
          let shouldRefresh = false;
          mutations.forEach(mutation => {
            if (mutation.type === 'childList' || (mutation.type === 'attributes' && mutation.attributeName === 'class')) {
              shouldRefresh = true;
            }
          });
          if (shouldRefresh) handleCartChange();
        });

        this.drawerObserver.observe(cartDrawer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      }

      const cartContainers = document.querySelectorAll(
        '#main-cart-items, #CartDrawer-CartItems, .cart-items, .cart__footer, #CartDrawer-Footer, .cart-drawer__footer'
      );

      if (cartContainers.length && !this.cartObserver) {
        this.cartObserver = new MutationObserver(debounce(() => {
          handleCartChange();
        }, 300));

        cartContainers.forEach(container => {
          this.cartObserver.observe(container, { childList: true, subtree: true });
        });
      }
    }

    async validateProductTotals() {
      const { validationResponseData } = this;

      if (!validationResponseData) {
        this.productTotalsValid = true;
        return { isValid: true };
      }

      const subscriptionProductTotal = validationResponseData.subscriptionProductTotal || 0;
      const regularProductTotal = validationResponseData.regularProductTotal || 0;

      if (subscriptionProductTotal === 0 && regularProductTotal === 0) {
        this.productTotalsValid = true;
        return { isValid: true };
      }

      try {
        const cart = await this.getCartData();
        const cartItems = cart.items || [];

        let subscriptionTotal = 0;
        let regularTotal = 0;

        cartItems.forEach(item => {
          const hasSubscriptionProperty = item.properties?._is_subscription === true || item.properties?._is_subscription === 'true';
          const hasSellingPlan = !!item.selling_plan_allocation;
          const isSubscription = hasSubscriptionProperty || hasSellingPlan;

          const lineTotal = item.final_line_price / 100;

          if (isSubscription) {
            subscriptionTotal += lineTotal;
          } else {
            regularTotal += lineTotal;
          }
        });

        const errors = [];

        if (subscriptionProductTotal > 0 && subscriptionTotal > 0) {
          if (subscriptionTotal < subscriptionProductTotal) {
            const needed = subscriptionProductTotal - subscriptionTotal;
            errors.push({
              type: 'subscription',
              required: subscriptionProductTotal,
              current: subscriptionTotal,
              needed: needed
            });
          }
        }

        if (regularProductTotal > 0 && regularTotal > 0) {
          if (regularTotal < regularProductTotal) {
            const needed = regularProductTotal - regularTotal;
            errors.push({
              type: 'regular',
              required: regularProductTotal,
              current: regularTotal,
              needed: needed
            });
          }
        }

        if (errors.length > 0) {
          this.productTotalsValid = false;
          return {
            isValid: false,
            errors: errors
          };
        }

        this.productTotalsValid = true;
        return { isValid: true };
      } catch (error) {
        console.error('Error validating product totals:', error);
        this.productTotalsValid = true;
        return { isValid: true };
      }
    }

    async validateCartTotal() {
      const { validationResponseData, minimumOrderAmount, cartTotal, currentPostcode } = this;

      if (!validationResponseData) return;

      const productTotalsValidation = await this.validateProductTotals();

      if (!productTotalsValidation.isValid && productTotalsValidation.errors) {
        const errorMessages = productTotalsValidation.errors.map(error => {
          if (error.type === 'subscription') {
            const template = this.messageSettings.minimumSubscriptionAmount;
            const message = this.replaceMessagePlaceholders(template, {
              'minimum subscription amount': error.required.toFixed(2),
              'current subscription amount': error.current.toFixed(2),
              'remaining subscription amount': error.needed.toFixed(2)
            });
            return message.replace(/\n/g, '<br>');
          } else {
            const template = this.messageSettings.minimumOneTimeAmount;
            const message = this.replaceMessagePlaceholders(template, {
              'minimum one-time amount': error.required.toFixed(2),
              'current one-time amount': error.current.toFixed(2),
              'remaining one-time amount': error.needed.toFixed(2)
            });
            return message.replace(/\n/g, '<br>');
          }
        });

        this.showMessage(errorMessages.join('<br><br>'), 'info');
        this.updateDeliveryDateState();
        return;
      }

      const successMessage = `Postcode ${currentPostcode} validated successfully.`;

      if (minimumOrderAmount === 0) {
        this.showMessage(successMessage, 'success', validationResponseData);
        this.updateDeliveryDateState();
        return;
      }

      if (cartTotal < minimumOrderAmount) {
        const balance = minimumOrderAmount - cartTotal;
        const template = this.messageSettings.minimumOrderAmount;
        const message = this.replaceMessagePlaceholders(template, {
          'minimum amount': minimumOrderAmount.toFixed(2),
          'current amount': balance.toFixed(2)
        });
        this.showMessage(message.replace(/\n/g, '<br>'), 'info');
        this.updateDeliveryDateState();
      } else {
        this.showMessage(successMessage, 'success', validationResponseData);
        this.updateDeliveryDateState();
      }
    }

    removeExpiredDateMessage() {
      const messageContainer = this.getElement('#message-container');
      const messages = messageContainer.querySelectorAll('.message.info');

      messages.forEach(message => {
        if (message.textContent.includes('Your delivery date has expired')) {
          message.remove();
          if (messageContainer.children.length === 0) {
            messageContainer.closest('.estimator-item').classList.remove('active');
          }
        }
      });
    }

    async clearShippingData() {
      this.currentPostcode = '';
      this.postcodeConfirmed = false;
      this.getElement('#postcode-display').value = '';

      const deliveryDateDisplay = this.getElement('#delivery-date-display');
      deliveryDateDisplay.textContent = 'Select Delivery Date';

      this.availableDates = [];
      this.renderCalendar();

      this.validationResponseData = null;
      this.minimumOrderAmount = 0;
      this.productTotalsValid = true;

      if (this.enableOutOfStock) {
        this.getElement('#out-of-stock-display').textContent = 'Please select option';
      }

      const messageContainer = this.getElement('#message-container');
      messageContainer.innerHTML = '';
      messageContainer.closest('.estimator-item').classList.remove('active');

      this.updateDeliveryDateState();

    }



    async clearDeliveryDate() {
      const deliveryDateDisplay = this.getElement('#delivery-date-display');
      deliveryDateDisplay.textContent = 'Select Delivery Date';

      this.availableDates = [];
      this.renderCalendar();


    }

    formatCartItemsForAPI(items) {
      if (!items || items.length === 0) {
        return '';
      }
      return items.map(item => `${item.variant_id}:${item.quantity}`).join(',');
    }

    async updateCartAttributes(attributes, retries = 3) {
      return this.enqueueRequest(async () => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const response = await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ attributes })
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Cart update failed with status: ${response.status}`);
            }

            return await response.json();
          } catch (error) {
            if (attempt < retries) {
              await new Promise(resolve => setTimeout(resolve, 300 * attempt));
              continue;
            }
            throw new Error(`Unable to update cart attributes after ${retries} attempts. Please refresh the page and try again.`);
          }
        }
      });
    }

    setLoading(loading) {
      this.isLoading = loading;
      const estimator = this.shadowRoot.querySelector('.shipping-estimator');
      estimator.classList.toggle('loading', loading);
    }

    showMessage(text, type = 'info', responseData = null) {
      const messageContainer = this.getElement('#message-container');
      messageContainer.closest('.estimator-item').classList.add('active');
      const message = document.createElement('div');
      message.className = `message ${type}`;

      messageContainer.innerHTML = '';

      if (responseData && responseData.isValid) {
        const heading = document.createElement('div');
        heading.classList.add('delivery-group');
        heading.textContent = responseData.deliveryGroup;

        const body = document.createElement('div');
        body.classList.add('delivery-message');
        body.textContent = responseData.deliveryMessage;

        message.appendChild(heading);
        message.appendChild(body);
      } else {
        const formattedText = text.replace(/\n/g, '<br>');
        message.innerHTML = formattedText;
      }

      messageContainer.appendChild(message);
    }

    navigateMonth(direction) {
      const newDate = new Date(this.currentDate);
      newDate.setMonth(newDate.getMonth() + direction);

      const today = new Date();
      const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const targetMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);

      if (targetMonth < currentMonth) {
        return;
      }

      this.currentDate = newDate;
      this.renderCalendar();
      this.updateNavigationButtons();
    }

    setCalendarToEarliestAvailableDate() {
      if (!this.availableDates || this.availableDates.length === 0) {
        const today = new Date();
        this.currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return;
      }

      let earliestDate = null;

      for (const dateString of this.availableDates) {
        const [day, month, year] = dateString.split('-').map(num => parseInt(num, 10));
        const date = new Date(year, month - 1, day);

        const today = new Date();
        if (date < today) continue;

        if (!earliestDate || date < earliestDate) {
          earliestDate = date;
        }
      }

      if (earliestDate) {
        this.currentDate = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), earliestDate.getDate());
      } else {
        const today = new Date();
        this.currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      }
    }

    formatDateForDisplay(dateString) {
      const [day, month, year] = dateString.split('-').map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    parseDateString(dateString) {
      if (dateString.includes(',')) {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    }

    formatDateForCart(dateString) {
      const [day, month, year] = dateString.split('-');
      const shortYear = year.toString().slice(-2);
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = month.padStart(2, '0');
      return `${paddedDay}/${paddedMonth}/${shortYear}`;
    }

    updateNavigationButtons() {
      const prevMonthBtn = this.getElement('#prev-month');
      const nextMonthBtn = this.getElement('#next-month');

      const today = new Date();
      const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const displayedMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);

      prevMonthBtn.disabled = displayedMonth.getTime() === currentMonth.getTime();

      const maxMonth = new Date(today.getFullYear(), today.getMonth() + 12, 1);
      nextMonthBtn.disabled = displayedMonth.getTime() >= maxMonth.getTime();
    }

    renderCalendar() {
      const monthYear = this.getElement('#month-year');
      const calendarGrid = this.getElement('#calendar-grid');

      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      monthYear.textContent = `${this.currentDate.toLocaleString('default', { month: 'long' })} ${year}`;

      calendarGrid.innerHTML = '';

      const dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
      dayHeaders.forEach(day => {
        const header = document.createElement('div');
        header.className = 'calendar-header';
        header.textContent = day;
        calendarGrid.appendChild(header);
      });

      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      for (let i = 0; i < firstDay; i++) {
        const emptyDay = document.createElement('div');
        emptyDay.className = 'calendar-day disabled';
        calendarGrid.appendChild(emptyDay);
      }

      const currentSelectedDate = this.getElement('#delivery-date-display').textContent;
      const parsedSelectedDate = this.parseDateString(currentSelectedDate);
      const today = new Date();

      for (let day = 1; day <= daysInMonth; day++) {
        const dayElement = document.createElement('div');
        dayElement.className = 'calendar-day';
        dayElement.textContent = day;

        const dateString = `${day}-${month + 1}-${year}`;
        const isAvailable = this.availableDates.includes(dateString);
        const isSelected = dateString === parsedSelectedDate;

        const currentDate = new Date(year, month, day);
        const isPastDate = currentDate < today;

        if (isSelected) {
          dayElement.classList.add('selected');
          dayElement.addEventListener('click', (e) => {
            e.stopPropagation();
            this.syncCartAttributesToBSS(this.currentPostcode, dateString, new Date().toISOString());
            this.selectDate(dateString);
          });
        } else if (isAvailable && !isPastDate) {
          dayElement.classList.add('available');
          dayElement.addEventListener('click', (e) => {
            e.stopPropagation();
            this.syncCartAttributesToBSS(this.currentPostcode, dateString, new Date().toISOString());
            this.selectDate(dateString);
          });
        } else {
          dayElement.classList.add('disabled');
          dayElement.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        }

        calendarGrid.appendChild(dayElement);
      }

      this.updateNavigationButtons();
    }

    async selectDate(dateString) {
      const formattedDate = this.formatDateForDisplay(dateString);
      this.getElement('#delivery-date-display').textContent = formattedDate;
      this.renderCalendar();
      this.closeAllDropdowns();

      if (typeof this.removeExpiredDateMessage === 'function') {
        this.removeExpiredDateMessage();
      }

      if (this.validationResponseData && this.currentPostcode) {
        await this.getCartData();
        await this.validateCartTotal();
      }

      try {
        await this.updateCartAttributes({
          'Delivery-Date': dateString,
          'Postcode': this.currentPostcode,
          'Delivery-Date-Request-Time': new Date().toISOString()
        });
        this.hasCriticalError = false;
        
      } catch (error) {
        this.hasCriticalError = true;
        this.showMessage(
          `Unable to save delivery date. Please refresh the page and try again, or contact support if the issue persists.`,
          'error'
        );
      }

      this.updateCheckoutButtonState();
      setTimeout(() => {
        this.updateCheckoutButtonState();
      }, 100);

      this.dispatchEvent(new CustomEvent('dateSelected', {
        detail: { date: dateString }
      }));
    }

    syncCartAttributesToBSS(postcode, deliverydate, deliveryDateRequestTime) {
        window.bssB2BHooks = window.bssB2BHooks || {
          actions: {},
          filters: {},
        };
        window.BSS_B2B = window.BSS_B2B || {};
        window.BSS_B2B.addAction = (tag, callback) => {
          window.bssB2BHooks.actions[tag] = callback;
        };
        window.BSS_B2B.addFilter = (tag, value) => {
          window.bssB2BHooks.filters[tag] = value;
        };

        window.BSS_B2B.addFilter("custom:order_additional_details/checkout",(customFieldValue, context) => {
            customFieldValue.push(
            { key: 'Postcode', value: postcode || '' },
            { key: 'Delivery-Date', value: deliverydate || '' },
            { key: 'Delivery-Date-Request-Time', value: deliveryDateRequestTime || '' }
          );
          return customFieldValue;
          //console.log(customFieldValue); 
        });
    }

    async updateOutOfStockPreference(value, text) {
      if (!this.enableOutOfStock) return;

      this.getElement('#out-of-stock-display').textContent = text;

      if (value) {
        try {
          await this.updateCartAttributes({
            'Out-Of-Stock-Preference': value
          });
          this.hasCriticalError = false;
        } catch (error) {
          this.hasCriticalError = true;
          this.showMessage(
            `Unable to save out of stock preference. Please refresh the page and try again, or contact support if the issue persists.`,
            'error'
          );
          this.updateCheckoutButtonState();
        }
      }

      this.dispatchEvent(new CustomEvent('outOfStockPreferenceChanged', {
        detail: { value, text }
      }));
    }

    updateDeliveryDateState() {
      const deliveryDateItem = this.getElement('#delivery-date-item');
      const postcodeInput = this.getElement('#postcode-display');

      const hasValidPostcode = this.postcodeConfirmed && postcodeInput.value.trim();

      const productTotalsValid = this.productTotalsValid;

      const meetsMinimumAmount = this.minimumOrderAmount <= 0 || this.cartTotal >= this.minimumOrderAmount;

      if (hasValidPostcode && productTotalsValid && meetsMinimumAmount) {
        deliveryDateItem.classList.remove('disabled');
      } else {
        deliveryDateItem.classList.add('disabled');
      }
    }



    async handleCheckoutClick(event, checkoutButton) {
      if (!checkoutButton) {
        return;
      }

      if (!this.isValidForCheckout()) {
        return;
      }

      const isLinkCheckout = checkoutButton.tagName === 'A' && checkoutButton.href;
      let form = checkoutButton.closest('form');

      if (!form && checkoutButton.form) {
        form = checkoutButton.form;
      }

      if (form && form.dataset.shippingEstimatorProcessing === 'true') {
        return;
      }

      if (!form && !isLinkCheckout) {
        return;
      }

      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }

      try {
        if (form) {
          form.dataset.shippingEstimatorProcessing = 'true';
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(checkoutButton);
          } else {
            form.submit();
          }
        } else if (isLinkCheckout) {
          window.location.href = checkoutButton.href;
        }
      } catch (error) {
        this.showMessage(
          'Unable to save delivery details. Please refresh the page and try again, or contact support if the issue persists.',
          'error'
        );
        this.updateCheckoutButtonState();
      } finally {
        if (form) {
          delete form.dataset.shippingEstimatorProcessing;
        }
      }
    }

    async updateCheckoutButtonState() {
      const checkoutButtons = document.querySelectorAll('.cart__checkout-button');

      if (!checkoutButtons.length) {
        setTimeout(() => this.updateCheckoutButtonState(), 100);
        return;
      }

      const isValidForCheckout = this.isValidForCheckout();

      checkoutButtons.forEach(button => {
        if (isValidForCheckout) {
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
          button.style.pointerEvents = "unset";
          button.removeAttribute('aria-disabled');
        } else {
          button.disabled = true;
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
          button.style.pointerEvents = "none";
          button.setAttribute('aria-disabled', 'true');
        }

        if (!button.dataset.shippingEstimatorBound) {
          button.addEventListener('click', (event) => {
            this.handleCheckoutClick(event, button);
          });
          button.dataset.shippingEstimatorBound = 'true';
        }
      });
    }

    isValidForCheckout() {
      if (this.hasCriticalError) {
        return false;
      }

      const deliveryDateDisplay = this.getElement('#delivery-date-display');
      const hasValidDate = deliveryDateDisplay.textContent &&
        deliveryDateDisplay.textContent !== 'Select Delivery Date' &&
        deliveryDateDisplay.textContent.trim() !== '';

      const postcodeInput = this.getElement('#postcode-display');
      const hasValidPostcode = postcodeInput &&
        postcodeInput.value &&
        postcodeInput.value.trim().length > 0;

      const meetsMinimumAmount = this.minimumOrderAmount <= 0 || this.cartTotal >= this.minimumOrderAmount;
      const productTotalsValid = this.productTotalsValid;

      return hasValidDate && hasValidPostcode && meetsMinimumAmount && productTotalsValid;
    }

    async checkCartDrawerState() {
      if (this.hasCheckedExpiredDate) {
        return;
      }

      await this.getCartData();

      const deliveryDateDisplay = this.getElement('#delivery-date-display');
      const currentSelectedDate = deliveryDateDisplay.textContent;
      const hasValidDate = currentSelectedDate &&
        currentSelectedDate !== 'Select Delivery Date' &&
        currentSelectedDate !== '';

      if (this.deliveryDateRequestTime) {
        const requestTime = new Date(this.deliveryDateRequestTime);
        const currentTime = new Date();
        const hoursDiff = (currentTime - requestTime) / (1000 * 60 * 60);

        if (hoursDiff > 1) {
          this.hasCheckedExpiredDate = true;

          deliveryDateDisplay.textContent = 'Select Delivery Date';


          setTimeout(() => {
            this.showMessage('Your delivery date has expired.\nPlease select a new date.', 'info');
          }, 1000);

          if (this.currentPostcode) {
            this.validatePostcode(this.currentPostcode).catch(error => {
              console.error('Failed to re-validate postcode:', error);
            });
          }
          return;
        }
      }

      if (this.validationResponseData && this.currentPostcode) {
        await this.validateCartTotal();
      } else if (!hasValidDate && this.postcodeConfirmed && this.currentPostcode) {
        setTimeout(() => {
          this.showMessage('Please select a delivery date to proceed with checkout.', 'info');
        }, 1000);
      }

      this.hasCheckedExpiredDate = true;
      this.updateCheckoutButtonState();
    }
  }

  if (!customElements.get('shipping-estimator')) {
    customElements.define('shipping-estimator', ShippingEstimator);
  }
}
